import { cx } from '@apparel/react/cx';
import { CartService } from '@apparel/ui/cart';
import { Image } from '@apparel/ui/images/Image';
import { Button } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Icon from '@material-ui/core/Icon';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded';
import { transparentize } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { GoogleTagService } from '../lib/GoogleTag';
import { GTag } from '../lib/utils';
import { CustomizableWheel as CustomWheel } from './CustomizableWheel';

const useStyles = makeStyles((theme) => ({
    box: {
        width: '100%',
        minWidth: '231px',
        display: 'inline-flex',
        padding: '7px 12px 7px 3px',
        cursor: 'pointer',

        [theme.breakpoints.down('xs')]: {
            padding: '7px 7px 4px 3px',
        },
    },
    selected: {
        border: '4px solid #e4002b !important',

        '&:hover': {
            boxShadow: `0 0 0 0 !important`,
            borderColor: '#e4002b !important',
        },
    },
    checked: {
        color: '#e4002b',
        position: 'absolute',
        top: '14px',
        right: '16px',
        zIndex: 5,
    },
    productCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        wordWrap: 'break-word',
        backgroundColor: '#fff',
        backgroundClip: 'border-box',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'box-shadow 250ms ease-out, border-color 250ms ease-out',
        width: '100%',
        minWidth: '232px',
        height: '313px',
        border: '1px solid lightgray',
        textDecoration: 'none !important',
        color: 'rgba(0, 0, 0, 0.87)',

        '& *': {
            textDecoration: 'none !important',
        },

        '&:hover': {
            borderColor: transparentize('0.25', '#e4002b'),
            boxShadow: `0 0 0 3px ${transparentize(0.5, '#e4002b')}`,

            '&:hover $layer': {
                opacity: 1,
                pointerEvents: 'all',
                '& button': {
                    transform: 'translate3d(0, 0, 0)',
                    opacity: 1,
                },
            },
        },
    },
    productCardBody: {
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 12px',

        [theme.breakpoints.down('xs')]: {
            padding: '8px',
        },
    },
    productCardTitle: {
        textAlign: 'left',
        margin: 0,
        letterSpacing: '0.21px',
        color: 'rgba(0, 0, 0, 0.87)',
        fontFamily: "'Roboto', sans-serif",
        fontSize: '12px',
        fontWeight: 400,
        marginBottom: '6px',
        [theme.breakpoints.down('xs')]: {
            marginBottom: '2px',
        },
    },
    minPrice: {
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.15px',
        color: '#e4002b',
        display: 'flex',
        alignContent: 'center',
        alignItems: 'center',
        margin: 0,
        opacity: 1,
        marginRight: '4px',
        fontSize: '16px',
    },
    priceInfo: {
        fontFamily: "'Roboto', sans-serif",
        fontSize: '14px',
        fontWeight: 400,

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        lineHeight: '20px',
        letterSpacing: '0.24px',
        color: 'rgba(0, 0, 0, 0.54)',
        opacity: 1,
        flexWrap: 'wrap',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',

        '& span, small, b': {
            whiteSpace: 'nowrap',
        },

        '& small': {
            fontFamily: "'Roboto', sans-serif",
            fontSize: '12px',
            fontWeight: 400,
            '&:first-child': {
                marginLeft: 0,
                marginRight: '2px',
            },
        },

        '& b': {
            color: 'rgba(0, 0, 0, 0.84)',
        },
    },
    priceSection: {
        marginTop: '20px',
    },
    layer: {
        zIndex: 99,
        position: 'absolute',
        display: 'flex',
        alignContent: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        top: '-2px',
        left: '-2px',
        bottom: '-2px',
        right: '-2px',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        opacity: 0,
        transition: 'opacity 250ms',
        pointerEvents: 'none',
    },
    addToCartButton: {
        transition: 'opacity 250ms, transform 400ms',
        transitionDelay: '150ms',
        transform: 'translate3d(0, 14px, 0)',

        opacity: 0,
    },
    productImageHolder: {
        display: 'flex',
        overflow: 'hidden',
        width: '100%',
        backgroundColor: '#fff',
        position: 'relative',
        borderBottom: '1px solid #eee',
        height: '192px',
        padding: '8px',
        '& p': {
            color: 'transparentize(#000, 0.36)',
            position: 'absolute',
            top: '50%',
            left: '50%',
            margin: '0',
            textTransform: 'uppercase',
            transform: 'translate(-50%, -50%)',
            fontSize: '14px',
            fontWeight: 400,
        },
    },
    productImage: {
        borderTopLeftRadius: 'calc(0.25rem - 1px)',
        borderTopRightRadius: 'calc(0.25rem - 1px)',

        position: 'absolute',
        objectFit: 'contain',
        objectPosition: 'center center',
        width: '100%',
        height: '100%',
        padding: '8px',

        [theme.breakpoints.down('xs')]: {
            padding: '4px',
        },
    },
}));

function getProductUrl(product) {
    const modelType = product.prices[0]?.model_type;
    const categorySlug = product.category.slug ?? product.category.id;
    const subcategorySlug = modelType === 'Wooter\\WApparelPackage' ? 'packages' : product.subcategory?.slug;
    const productSlug = product.slug ?? product.id;
    return `/marketplace/${categorySlug}/${subcategorySlug ?? 'other'}/${productSlug}`;
}

export function formatPrice(price) {
    const normalPrice = (Math.round(price * 100) / 100).toString();

    if (!normalPrice.includes('.')) {
        return '$' + normalPrice + '.00';
    }

    const numbersAfterFractions = normalPrice.split('.')[1];

    if (numbersAfterFractions.length === 1) {
        return '$' + normalPrice + '0';
    }
    return '$' + normalPrice;
}

const ProductImage = ({ product, fromDR }) => {
    const modelType = fromDR ? null : product.prices[0]?.model_type;

    const addToCart = (e) => {
        e.preventDefault();
        GoogleTagService.reportConversion(GTag.Add_to_cart);
        const cart = CartService.getInstance();
        const quantity = Math.max(product.minimum_order_quantity, 1);
        const id = product.id;
        switch (modelType) {
            case 'Wooter\\WApparelPackage':
                cart.addPackage({
                    id: parseInt(id),
                    category_id: product.category?.id,
                    quantity: quantity,
                    model_type: 'package',
                    subcategories: product.subcategories,
                });
                break;
            default:
                cart.addItem({
                    quantity: quantity,
                    sku_id: parseInt(id),
                    model_id: parseInt(id),
                    model_type: 'sku',
                    category_id: product.category.id,
                    subcategory_id: product.subcategory.id,
                });
        }
        cart.emitOpenCart();
    };

    const classes = useStyles({});
    return (
        <div className={classes.productImageHolder}>
            <Image
                source={product.preview_photo?.thumbnail_path ?? product.image?.thumbnail_path}
                objectFit={'contain'}
                objectPosition={'center'}
            />
            {!fromDR && (
                <div className={classes.layer}>
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        onClick={addToCart}
                        className={classes.addToCartButton}
                    >
                        Add to Cart
                    </Button>
                </div>
            )}
        </div>
    );
};

export const ProductBody = ({ product }) => {
    const classes = useStyles({});

    let maxPrice = '';
    let minPrice = '';
    const minOrder = product?.minimum_order_quantity ? product?.minimum_order_quantity : 1;

    (product?.prices || product.customer_custom_prices).forEach((price) => {
        if (price.minimum <= minOrder && price.maximum >= minOrder) {
            maxPrice = formatPrice(price.customer_price);
        }
        if (price.customer_price > 0) {
            minPrice = formatPrice(price.customer_price);
        }
    });

    return (
        <div className={cx('card-body', classes.productCardBody)}>
            <h3 className={cx('card-title', classes.productCardTitle)} title={product?.name}>
                <b>{product?.name}</b>
            </h3>

            <div className="yotpo bottomLine" data-yotpo-product-id={product.yotpo_review ?? -1} />
            <section className={classes.priceSection}>
                <div className={classes.priceInfo}>
                    <p className={classes.minPrice}>{minPrice} </p>
                    <small className={'text-mute ml-0'}>{'(1,000+)'}</small>
                </div>
                <div className={classes.priceInfo}>
                    <small className={'text-mute ml-0'}>{`Min. Quantity: ${minOrder}`}</small>
                    <small className={'text-mute ml-0'}>{`(${maxPrice}/Piece)`}</small>
                </div>
            </section>
        </div>
    );
};

function ProductCard(props) {
    const { product, fromDR, handleClick, selected, CustomizableWheel } = props;
    const classes = useStyles(props);
    const isMobile = useMediaQuery('(max-width: 599px)');

    if (isMobile && fromDR) {
        return <HorizontalProductCard {...{ product, handleClick, selected, CustomizableWheel }} />;
    }

    const productUrl = fromDR ? '' : getProductUrl(product);
    return (
        <Box className={classes.box} key={product.id} p={1}>
            {fromDR ? (
                <div className={cx(selected ? classes.selected : '', classes.productCard)} onClick={handleClick}>
                    {CustomizableWheel}
                    {selected ? (
                        <Icon className={classes.checked}>check_circle</Icon>
                    ) : (
                        <AddCircleOutlineRoundedIcon className={classes.checked} />
                    )}
                    <ProductImage product={product} fromDR />
                    <ProductBody product={product} />
                </div>
            ) : (
                <a href={productUrl} className={classes.productCard}>
                    <CustomPlaceholder>
                        <CustomWheel />
                    </CustomPlaceholder>
                    <ProductImage product={product} fromDR={false} />
                    <ProductBody product={product} />
                </a>
            )}
        </Box>
    );
}

const CustomPlaceholder = styled.div`
    position: absolute;
    top: 9px;
    left: 9px;
    z-index: 5;
`;

export function pricing(product) {
    let maxPrice = '';
    let minPrice = '';
    let priceRange10to25 = '';
    let minPriceQuantity = 0;
    const minOrder = product.minimum_order_quantity || 1;

    (product?.customer_custom_prices || product?.prices).forEach((price) => {
        if (price.minimum <= minOrder && price.maximum >= minOrder) {
            maxPrice = formatPrice(price.customer_price);
        }
        if (price.minimum === 10 && price.maximum === 25) {
            priceRange10to25 = formatPrice(price.customer_price);
        }
        if (price.customer_price > 0) {
            minPrice = formatPrice(price.customer_price);
            minPriceQuantity = parseInt(price.minimum);
        }
    });

    return { minPrice, maxPrice, minOrder, minPriceQuantity, priceRange10to25 };
}

function HorizontalProductCard({ product, handleClick, selected }) {
    const classes = useStyles();

    const { minPrice, minPriceQuantity, maxPrice, minOrder } = pricing(product);

    return (
        <Box width={'100%'} padding={'6px 0'}>
            <div
                style={{ height: '130px', flexDirection: 'row', borderRadius: '2px' }}
                className={classes.productCard}
                onClick={handleClick}
            >
                {selected ? (
                    <Icon className={classes.checked}>check_circle</Icon>
                ) : (
                    <AddCircleOutlineRoundedIcon className={classes.checked} />
                )}
                <ImagesPlaceholder>
                    <Image
                        source={product.preview_photo?.thumbnail_path ?? product.image?.thumbnail_path}
                        objectFit={'contain'}
                        objectPosition={'center'}
                    />
                </ImagesPlaceholder>
                <div style={{ width: '100%', padding: '12px' }}>
                    <Typography gutterBottom variant={'body2'} style={{ fontSize: '12px' }}>
                        {product.name}
                    </Typography>
                    <div className={classes.priceInfo}>
                        <p className={classes.minPrice}>{minPrice} </p>
                        <small className={'text-mute ml-0'}> {`(${minPriceQuantity - 1}+)`}</small>
                    </div>
                    <div className={classes.priceInfo}>
                        <small className={'text-mute ml-0'}>{`Min. Quantity: ${minOrder}`}</small>
                        <small className={'text-mute ml-0'}>{`(${maxPrice}/Package)`}</small>
                    </div>
                </div>
            </div>
        </Box>
    );
}

const ImagesPlaceholder = styled.div`
    width: 139px;
    border-right: 1px solid #dedede;
    display: flex;
    flex-direction: row;
    box-sizing: border-box;
`;

export default ProductCard;
